import { AlertErrorCircle, ChevronRight, Folder } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FolderResponse } from '@/user_files/folders/schemas'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { isSelectableProcessingStatus } from '@/user_files/utils/processing-status'
import clsx from 'clsx'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { UserFileTypeIcon } from '@/user_files/files/components/UserFileTypeIcon'
import { FolderSelectionCheckbox } from './FolderSelectionCheckbox'
import type { FolderSelectionState } from './useRecursiveFolderSelection'

const disabledFileTooltipOffset: [number, number] = [-20, -15]
const fileProcessingTooltipMessage = 'File is still processing. Try again shortly.'
const fileProcessingFailedMessage = 'Processing failed. Please try again.'
const folderSelectionErrorMessage = 'Unable to select files in this folder. Please try again.'

export type MatterFilesFileToggleOptions = {
  shiftKey?: boolean
}

export type MatterFilesFolderFileListProps = {
  /** Child folders of the browsed folder. */
  folders: FolderResponse[]
  /** Files in the browsed folder. */
  files: UserFileRead[]
  /** Open a subfolder by id. */
  onNavigateToFolder: (folderId: string) => void
  /** When true, folder row buttons are disabled (e.g. while move is submitting). */
  disabled?: boolean
  /** A list of file ids that are selected. */
  selectedFileIds?: string[]
  /** When provided, file rows are selectable. */
  onToggleFile?: (file: UserFileRead, options?: MatterFilesFileToggleOptions) => void
  /** Controls the input type for selectable rows. Defaults to 'checkbox'. */
  selectionVariant?: 'checkbox' | 'radio'
  /** Optional file-level selection rule. Defaults to allowing every loaded file. */
  isFileSelectable?: (file: UserFileRead) => boolean
  /** Optional tooltip shown for files rejected by `isFileSelectable`. */
  fileNotSelectableMessage?: string
  /** Get recursive selection state for a child folder row. */
  getFolderSelectionState?: (folderId: string) => FolderSelectionState
  /** Get tooltip copy for a child folder's recursive selection state. */
  getFolderSelectionTooltipMessage?: (folderId: string) => string | null
  /** Toggle recursive file selection for a child folder row. */
  onToggleFolderSelection?: (folder: FolderResponse) => void
}

export function MatterFilesFolderFileList({
  folders,
  files,
  onNavigateToFolder,
  disabled = false,
  selectedFileIds = [],
  onToggleFile,
  selectionVariant = 'checkbox',
  isFileSelectable,
  fileNotSelectableMessage,
  getFolderSelectionState,
  getFolderSelectionTooltipMessage,
  onToggleFolderSelection,
}: MatterFilesFolderFileListProps) {
  const hasItems = folders.length > 0 || files.length > 0
  const showFolderCheckboxes = Boolean(onToggleFile && onToggleFolderSelection && getFolderSelectionState && selectionVariant === 'checkbox')

  return (
    <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain -mb-px" role="region" aria-label="Folders and files in this location">
      <ul className={clsx('m-0 list-none divide-y divide-brand-neutral-200 p-0', hasItems && 'border-b border-brand-neutral-200')}>
        {folders.map((folder) => {
          const folderSelectionState = getFolderSelectionState?.(folder.id) ?? 'unchecked'
          const folderSelectionIsLoading = folderSelectionState === 'loading'
          const folderCheckboxTooltip = (() => {
            if (folderSelectionState === 'error') {
              return <span className="text-sm px-1 py-2 font-normal font-sans">{folderSelectionErrorMessage}</span>
            }
            return ''
          })()
          const folderSelectionTooltipMessage = folderSelectionState === 'partial-selected' ? getFolderSelectionTooltipMessage?.(folder.id) : null
          const folderRowTooltip = folderSelectionTooltipMessage ? (
            <span className="text-sm px-1 py-2 font-normal font-sans">{folderSelectionTooltipMessage}</span>
          ) : (
            ''
          )

          return (
            <li key={folder.id}>
              <CustomizableTooltip title={folderRowTooltip} placement="top-start" offset={disabledFileTooltipOffset} disableInteractive>
                <div className="flex h-12 w-full items-center gap-3 pl-6 pr-3 hover:bg-brand-neutral-50">
                  {showFolderCheckboxes ? (
                    <CustomizableTooltip title={folderCheckboxTooltip} placement="top-start" offset={disabledFileTooltipOffset} disableInteractive>
                      <label className={clsx('flex h-full shrink-0 items-center', folderSelectionIsLoading ? 'cursor-not-allowed' : 'cursor-pointer')}>
                        <FolderSelectionCheckbox
                          disabled={folderSelectionIsLoading}
                          folderName={folder.name}
                          onToggle={() => onToggleFolderSelection?.(folder)}
                          state={folderSelectionState}
                        />
                      </label>
                    </CustomizableTooltip>
                  ) : null}
                  <button
                    type="button"
                    className="flex h-full min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset"
                    onClick={() => onNavigateToFolder(folder.id)}
                    disabled={disabled}
                  >
                    <span className="relative h-6 w-6 shrink-0" aria-hidden="true">
                      <Folder className="h-6 w-6 shrink-0 text-brand-neutral-500" />
                      {folderSelectionIsLoading ? (
                        <span className="absolute inset-0 flex translate-y-0.5 items-center justify-center">
                          <CircularProgressContinuousSized size={10} thickness={8} showTrack />
                        </span>
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-brand-neutral-950" title={folder.name}>
                      {folder.name}
                    </span>
                    <ChevronRight className="h-5 w-5 shrink-0 text-brand-neutral-950" />
                  </button>
                </div>
              </CustomizableTooltip>
            </li>
          )
        })}
        {files.map((file) => {
          if (!onToggleFile) {
            return (
              <li key={file.id} className="flex h-12 items-center gap-3 pl-6 pr-3">
                <UserFileTypeIcon detectedContentType={file.detected_content_type} className="h-6 w-6 shrink-0 text-brand-neutral-500" />
                <span className="min-w-0 flex-1 truncate text-sm text-brand-neutral-950" title={file.original_filename}>
                  {file.original_filename}
                </span>
                <span className="w-5 shrink-0" aria-hidden="true" />
              </li>
            )
          }

          const isSelected = selectedFileIds.includes(file.id)
          const passesFileFilter = isFileSelectable?.(file) ?? true
          const processingStatus = file.processing_status
          const isProcessing = processingStatus === UserFileProcessingStatus.Pending || processingStatus === UserFileProcessingStatus.Processing
          const isFailed = processingStatus === UserFileProcessingStatus.Failed
          const isSelectable = isSelectableProcessingStatus(processingStatus) && passesFileFilter
          const isDisabled = !isSelectable && !isSelected
          const disabledTooltipMessage = (() => {
            if (isProcessing) return fileProcessingTooltipMessage
            if (isFailed) return file.processing_error ?? fileProcessingFailedMessage
            if (!passesFileFilter) return fileNotSelectableMessage
            return null
          })()
          const disabledTooltip = disabledTooltipMessage ? <span className="text-sm px-1 py-2 font-normal font-sans">{disabledTooltipMessage}</span> : ''

          return (
            <li key={file.id}>
              <CustomizableTooltip title={disabledTooltip} placement="top-start" offset={disabledFileTooltipOffset} disableInteractive>
                <label
                  className={clsx(
                    'flex h-12 w-full items-center gap-3 pl-6 pr-3',
                    isDisabled ? 'cursor-not-allowed text-brand-neutral-500' : 'cursor-pointer hover:bg-brand-neutral-50'
                  )}
                  onMouseDown={(event) => {
                    // Suppress text highlighting during Shift-click range selection
                    if (selectionVariant === 'checkbox' && event.shiftKey) {
                      event.preventDefault()
                    }
                  }}
                >
                  <input
                    type={selectionVariant}
                    name={selectionVariant === 'radio' ? 'file-selection' : undefined}
                    checked={isSelected}
                    disabled={isDisabled}
                    onClick={(event) => onToggleFile(file, selectionVariant === 'checkbox' ? { shiftKey: event.shiftKey } : undefined)}
                    onChange={() => undefined}
                    className={clsx(
                      'shrink-0 border-brand-neutral-300 text-brand-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
                      selectionVariant === 'radio' ? 'h-5 w-5 rounded-full' : 'h-4 w-4 rounded',
                      isDisabled ? 'cursor-not-allowed disabled:bg-brand-neutral-100' : 'cursor-pointer'
                    )}
                  />
                  {isProcessing ? (
                    <CircularProgressContinuousSized size={20} thickness={6} className="shrink-0" />
                  ) : isFailed ? (
                    <AlertErrorCircle className="h-6 w-6 shrink-0 rounded-full bg-red-600 text-white" />
                  ) : (
                    <UserFileTypeIcon
                      detectedContentType={file.detected_content_type}
                      className={clsx('h-6 w-6 shrink-0', isDisabled ? 'text-brand-neutral-400' : 'text-brand-neutral-500')}
                    />
                  )}
                  <span
                    className={clsx('min-w-0 flex-1 truncate text-sm', isDisabled ? 'text-brand-neutral-500' : 'text-brand-neutral-950')}
                    title={file.original_filename}
                  >
                    {file.original_filename}
                  </span>
                  <span className="w-5 shrink-0" aria-hidden="true" />
                </label>
              </CustomizableTooltip>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
